import { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { connect } from 'react-redux';
import { styled } from '../../../../shared/Theme/theme';
import stations from '../../../../../static/data/stations.json';
import Button from '../../../../shared/SmallComponents/Button';
import { localizationSelectors } from '../../../../../state/ducks/localization/selectors';
import { filterFieldsType } from '../../../../../state/ducks/allShipmentsPage/allShipmentsPage';
import { FormDatePicker, FormDropDown, FormTextInput } from '../../../../shared/inputs/form';
import FieldLabel from '../../../../shared/SmallComponents/FieldLabel';
import { Flex } from '../../../singleShipment/ShipmentForm/ShipmentForm';
import { shipmentsSelectors } from '../../../../../state/ducks/shipments/selectors';
import { RootState } from '../../../../../state/store/store';
import { defaultConsolidationFilters } from './consolidationsFilterTypes';

type Props = {
    localizations: any;
    filterFields: filterFieldsType;
    onCancel: () => void;
    onApplyFilters: (filter: filterFieldsType) => void;
    shipmentStateOptions: { text: string; value: string; color: string }[];
};

type formFilterFields = {
    state?: string[];
    originStation?: string;
    destinationStation?: string;
    flightVoyage?: string;
    fromETD?: Date;
    toETD?: Date;
    fromETA?: Date;
    toETA?: Date;
};

const ConsolidationsFilterContent = ({
    localizations,
    filterFields,
    onCancel,
    onApplyFilters,
    shipmentStateOptions
}: Props) => {
    const [formFieldsValues, setFormFieldsValues] = useState<formFilterFields>({});

    useEffect(() => {
        const vals: formFilterFields = {};
        filterFields.forEach(({ field, value }) => {
            (vals as any)[field] = value;
        });
        setFormFieldsValues(vals);
    }, [filterFields]);

    const stationOptions = useMemo(
        () =>
            stations
                .filter((s) => s.code)
                .map((station) => ({
                    text: station.code + (station.name ? ` - ${station.name}` : station.location ? ` - ${station.location}` : ''),
                    value: station.code
                })),
        []
    );

    const submit = () => {
        const filter: filterFieldsType = [];
        Object.keys(formFieldsValues).forEach((item) => {
            const value = (formFieldsValues as any)[item];
            if (value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : String(value).trim() !== '')) {
                filter.push({ field: item, value });
            }
        });
        onApplyFilters(filter);
        onCancel();
    };

    const resetForm = () => {
        setFormFieldsValues({});
        onApplyFilters(defaultConsolidationFilters);
    };

    return (
        <Formik initialValues={formFieldsValues} onSubmit={submit}>
            {() => (
                <FormContainer>
                    <Container>
                        {/* Status — matches Status column */}
                        <FieldContainer data-popover-bug>
                            <FieldLabel>Status</FieldLabel>
                            <FormDropDown
                                preventPopoverBug={true}
                                name="state"
                                placeHolder="Status"
                                options={shipmentStateOptions}
                                allowClear
                                mode="multiple"
                                onChange={(value: string[]) => setFormFieldsValues({ ...formFieldsValues, state: value })}
                                value={formFieldsValues.state}
                            />
                        </FieldContainer>

                        {/* Flight / Voyage — matches Flight/Voyage column */}
                        <FieldContainer>
                            <FieldLabel>Flight / Voyage</FieldLabel>
                            <FormTextInput
                                name="flightVoyage"
                                placeHolder="Flight number or voyage"
                                onChange={(value: string) => setFormFieldsValues({ ...formFieldsValues, flightVoyage: value })}
                                value={formFieldsValues.flightVoyage || ''}
                            />
                        </FieldContainer>

                        {/* Origin Station — builds the Route column */}
                        <FieldContainer data-popover-bug>
                            <FieldLabel>{localizations.origin_station}</FieldLabel>
                            <FormDropDown
                                showSearch
                                preventPopoverBug={true}
                                name="originStation"
                                placeHolder={localizations.origin_station}
                                options={stationOptions}
                                onChange={(value: string) => setFormFieldsValues({ ...formFieldsValues, originStation: value })}
                                value={formFieldsValues.originStation}
                                allowClear
                            />
                        </FieldContainer>

                        {/* Destination Station — builds the Route column */}
                        <FieldContainer data-popover-bug>
                            <FieldLabel>{localizations.destination_station}</FieldLabel>
                            <FormDropDown
                                showSearch
                                preventPopoverBug={true}
                                name="destinationStation"
                                placeHolder={localizations.destination_station}
                                options={stationOptions}
                                onChange={(value: string) => setFormFieldsValues({ ...formFieldsValues, destinationStation: value })}
                                value={formFieldsValues.destinationStation}
                                allowClear
                            />
                        </FieldContainer>

                        {/* ETD range — departure date, matches ETD column */}
                        <FieldContainer data-popover-bug>
                            <FieldLabel>ETD From</FieldLabel>
                            <FormDatePicker
                                name="fromETD"
                                placeHolder="ETD From"
                                onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, fromETD: value })}
                                value={formFieldsValues.fromETD}
                                preventPopoverBug={true}
                            />
                        </FieldContainer>
                        <FieldContainer data-popover-bug>
                            <FieldLabel>ETD To</FieldLabel>
                            <FormDatePicker
                                name="toETD"
                                placeHolder="ETD To"
                                onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, toETD: value })}
                                value={formFieldsValues.toETD}
                                preventPopoverBug={true}
                            />
                        </FieldContainer>

                        {/* ETA range — arrival date, matches ETA column */}
                        <FieldContainer data-popover-bug>
                            <FieldLabel>ETA From</FieldLabel>
                            <FormDatePicker
                                name="fromETA"
                                placeHolder="ETA From"
                                onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, fromETA: value })}
                                value={formFieldsValues.fromETA}
                                preventPopoverBug={true}
                            />
                        </FieldContainer>
                        <FieldContainer data-popover-bug>
                            <FieldLabel>ETA To</FieldLabel>
                            <FormDatePicker
                                name="toETA"
                                placeHolder="ETA To"
                                onChange={(value: Date) => setFormFieldsValues({ ...formFieldsValues, toETA: value })}
                                value={formFieldsValues.toETA}
                                preventPopoverBug={true}
                            />
                        </FieldContainer>
                    </Container>
                    <FormFooter>
                        <Button minWidth="55px" type="button" onClick={onCancel}>
                            <span>{localizations.cancel_btn}</span>
                        </Button>
                        <Flex>
                            <Button minWidth="55px" type="button" onClick={resetForm}>
                                <span>{localizations.reset_btn}</span>
                            </Button>
                            <Button minWidth="55px" buttonType="filled" type="submit">
                                <span>{localizations.filter_btn}</span>
                            </Button>
                        </Flex>
                    </FormFooter>
                </FormContainer>
            )}
        </Formik>
    );
};

const FormContainer = styled(Form)`
    min-width: 460px;

    @media screen and (max-width: 768px) {
        min-width: 100%;
    }
`;

const FieldContainer = styled.div`
    margin-bottom: 12px;
    flex-basis: 45%;
    width: 0;
    padding: 0 10px;

    @media screen and (max-width: 768px) {
        flex-basis: 100%;
    }
`;

const Container = styled.div`
    width: 100%;
    max-height: 420px;
    overflow: auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    padding-bottom: 80px;

    @media screen and (max-width: 768px) {
        height: calc(100vh - 70px);
        padding: 4px 12px;
        max-height: 100%;
    }
`;

const FormFooter = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: space-between;
    height: 70px;
    padding: 17px 30px;
    border-top: 1px solid #eaeaea;
    left: 0;
    right: 0;
    bottom: -70px;
    background: white;
    box-shadow: 0 9px 12px 1px rgb(0 0 0 / 9%);

    button {
        padding: 15px;
    }
`;

const mapStateToProps = (state: RootState) => ({
    localizations: localizationSelectors.allShipmentsPage(state).filter,
    shipmentStateOptions: shipmentsSelectors.shipmentStateOptions(state)
});

export default connect(mapStateToProps)(ConsolidationsFilterContent);
